// ABOUTME: Table compound component with semantic HTML elements
// ABOUTME: Provides Flowbite-compatible API with striped and hoverable options

import * as React from "react"
import { cn } from "@shared/lib/utils"

// Base Table components
const TableBase = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  ),
)
TableBase.displayName = "TableBase"

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  ),
)
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  ),
)
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn("border-t border-gray-200 bg-gray-50 font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  ),
)
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b border-gray-200 bg-white transition-colors", className)}
      {...props}
    />
  ),
)
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-10 px-2 text-left align-middle font-medium text-gray-500 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  ),
)
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "p-2 align-middle text-gray-900 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  ),
)
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption
      ref={ref}
      className={cn("mt-4 text-sm text-gray-500", className)}
      {...props}
    />
  ),
)
TableCaption.displayName = "TableCaption"

// CTable compound component with Flowbite-compatible API
export interface CTableProps extends React.HTMLAttributes<HTMLTableElement> {
  striped?: boolean
  hoverable?: boolean
  /** Disable horizontal scroll wrapper */
  noScroll?: boolean
}

export type CTableHeadProps = React.HTMLAttributes<HTMLTableSectionElement>
export type CTableBodyProps = React.HTMLAttributes<HTMLTableSectionElement>
export type CTableRowProps = React.HTMLAttributes<HTMLTableRowElement>
export type CTableCellProps = React.TdHTMLAttributes<HTMLTableCellElement>
export type CTableHeadCellProps = React.ThHTMLAttributes<HTMLTableCellElement>

type CTableComponent = React.FC<CTableProps> & {
  Head: React.FC<CTableHeadProps>
  Body: React.FC<CTableBodyProps>
  Row: React.FC<CTableRowProps>
  Cell: React.FC<CTableCellProps>
  HeadCell: React.FC<CTableHeadCellProps>
}

const CTableBase: React.FC<CTableProps> = ({ className, striped, hoverable, noScroll, ...props }) => {
  const tableElement = (
    <table
      className={cn(
        "w-full caption-bottom text-sm",
        striped && "[&_tbody_tr:nth-child(odd)]:bg-gray-50",
        hoverable && "[&_tbody_tr:hover]:bg-gray-100",
        className,
      )}
      {...props}
    />
  )

  if (noScroll) {
    return <div className="relative w-full">{tableElement}</div>
  }

  return <div className="relative w-full overflow-auto">{tableElement}</div>
}

const CTableHead: React.FC<CTableHeadProps> = ({ className, ...props }) => {
  return (
    <TableHeader
      className={cn("bg-gray-50", className)}
      {...props}
    />
  )
}

const CTableBody: React.FC<CTableBodyProps> = (props) => {
  return <TableBody {...props} />
}

const CTableRow: React.FC<CTableRowProps> = (props) => {
  return <TableRow {...props} />
}

const CTableCell: React.FC<CTableCellProps> = ({ className, ...props }) => {
  return (
    <TableCell
      className={cn("px-6 py-4", className)}
      {...props}
    />
  )
}

const CTableHeadCell: React.FC<CTableHeadCellProps> = ({ className, ...props }) => {
  return (
    <TableHead
      className={cn("px-6 py-3 text-xs font-medium uppercase text-gray-700", className)}
      {...props}
    />
  )
}

const CTableWithStatics: CTableComponent = Object.assign(CTableBase, {
  Head: CTableHead,
  Body: CTableBody,
  Row: CTableRow,
  Cell: CTableCell,
  HeadCell: CTableHeadCell,
})

// Exported under its own identifier: Fast Refresh (react-refresh/only-export-components)
// recognises a component by the name, and the Object.assign that attaches the statics is opaque to it.
export const CTable = CTableWithStatics

// Export base components for potential reuse
export { TableBase, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell, TableCaption }
